// WorldMap: Abstract geometric visualization of sealed vs unsealed techniques
import { useEffect, useState } from "react";
import { api, Technique, TechniqueProgress } from "@/lib/api";

interface TechniqueNode {
  technique: Technique;
  progress: TechniqueProgress | null;
  masteryLevel: "none" | "novice" | "beginner" | "intermediate" | "advanced" | "expert";
}

export function WorldMap() {
  const [nodes, setNodes] = useState<TechniqueNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTechnique, setHoveredTechnique] = useState<string | null>(null);

  useEffect(() => {
    loadWorldMap();
  }, []);

  const loadWorldMap = async () => {
    try {
      const [techniques, progress] = await Promise.all([
        api.getTechniques({ view: "list" }),
        api.getUserProgress().catch(() => []),
      ]);

      const allNodes: TechniqueNode[] = techniques.map((technique) => {
        const userProgress = progress.find(
          (p: TechniqueProgress) => p.technique_id === technique.id
        );
        return {
          technique,
          progress: userProgress || null,
          masteryLevel: userProgress?.mastery_level || "none",
        };
      });

      setNodes(allNodes);
    } catch (error) {
      console.error("Failed to load world map:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMasteryColor = (
    masteryLevel: string
  ): { fill: string; stroke: string; opacity: number } => {
    switch (masteryLevel) {
      case "expert":
        return { fill: "#ffffff", stroke: "#e5e7eb", opacity: 1 }; // White - sealed
      case "advanced":
        return { fill: "#f3f4f6", stroke: "#d1d5db", opacity: 0.9 }; // Light gray - nearly sealed
      case "intermediate":
        return { fill: "#9ca3af", stroke: "#6b7280", opacity: 0.7 }; // Gray - in progress
      case "beginner":
      case "novice":
        return { fill: "#4b5563", stroke: "#374151", opacity: 0.5 }; // Dark gray - started
      default:
        return { fill: "#000000", stroke: "#374151", opacity: 0.9 }; // Black - chaos
    }
  };

  const getNodeRadius = (masteryLevel: string): number => {
    // Sealed techniques are slightly larger
    return masteryLevel === "expert" || masteryLevel === "advanced" ? 14 : 12;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border-2 border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          World Map
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      </div>
    );
  }

  // Compute dynamic SVG dimensions based on total technique count
  const totalNodes = nodes.length;
  const cols = Math.ceil(Math.sqrt(totalNodes * 1.5)); // slightly wider than tall
  const rows = Math.ceil(totalNodes / cols);
  const spacingX = 48;
  const spacingY = 44;
  const padX = 40;
  const padY = 40;
  const svgWidth = padX * 2 + cols * spacingX;
  const svgHeight = padY * 2 + rows * spacingY;

  const sealedCount = nodes.filter(
    (n) => n.masteryLevel === "expert" || n.masteryLevel === "advanced"
  ).length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border-2 border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            World Map: Ancient Forms
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Each node is a technique. Seal the chaos by mastering all forms.
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {sealedCount}/{totalNodes}
          </p>
          <p className="text-xs text-gray-500">Sealed</p>
        </div>
      </div>

      {/* SVG Visualization - all techniques in one hex grid */}
      <div className="relative overflow-x-auto">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-auto"
          style={{ minHeight: "300px", maxHeight: "700px" }}
        >
          {/* Background grid pattern */}
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="0.5"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect width={svgWidth} height={svgHeight} fill="url(#grid)" />

          {/* Render all techniques in a single hex grid */}
          {nodes.map((node, index) => {
            const row = Math.floor(index / cols);
            const col = index % cols;
            const hexOffset = row % 2 === 0 ? 0 : spacingX / 2;
            const x = padX + col * spacingX + hexOffset;
            const y = padY + row * spacingY;

            const { fill, stroke, opacity } = getMasteryColor(
              node.masteryLevel
            );
            const nodeRadius = getNodeRadius(node.masteryLevel);

            const isSealed =
              node.masteryLevel === "expert" ||
              node.masteryLevel === "advanced";
            const isHovered = hoveredTechnique === node.technique.id;

            return (
              <g
                key={node.technique.id}
                onMouseEnter={() =>
                  setHoveredTechnique(node.technique.id)
                }
                onMouseLeave={() => setHoveredTechnique(null)}
                className="cursor-pointer transition-transform hover:scale-110"
              >
                {/* Glow effect for sealed techniques */}
                {isSealed && (
                  <circle
                    cx={x}
                    cy={y}
                    r={nodeRadius + 6}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="2"
                    opacity="0.3"
                    className="animate-pulse"
                  />
                )}

                {/* Pulse animation for unmastered techniques */}
                {node.masteryLevel === "none" && (
                  <circle
                    cx={x}
                    cy={y}
                    r={nodeRadius + 4}
                    fill="none"
                    stroke="#000000"
                    strokeWidth="1"
                    opacity="0.2"
                    className="animate-chaos-pulse"
                  />
                )}

                {/* Main node circle */}
                <circle
                  cx={x}
                  cy={y}
                  r={nodeRadius}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="2"
                  opacity={isHovered ? 1 : opacity}
                  className={`transition-all duration-300 ${isSealed ? "animate-world-heal" : ""}`}
                />

                {/* Checkmark for sealed techniques */}
                {isSealed && (
                  <text
                    x={x}
                    y={y + 5}
                    textAnchor="middle"
                    className="text-xs font-bold fill-green-600"
                  >
                    ✓
                  </text>
                )}

                {/* Hover tooltip */}
                {isHovered && (
                  <g>
                    <rect
                      x={x - 80}
                      y={y - 50}
                      width="160"
                      height="40"
                      fill="#1f2937"
                      rx="4"
                      opacity="0.95"
                    />
                    <text
                      x={x}
                      y={y - 35}
                      textAnchor="middle"
                      className="text-xs font-semibold fill-white"
                    >
                      {node.technique.name}
                    </text>
                    <text
                      x={x}
                      y={y - 20}
                      textAnchor="middle"
                      className="text-xs fill-gray-300"
                    >
                      {node.masteryLevel === "none"
                        ? "Not started"
                        : node.masteryLevel === "expert"
                          ? "Sealed ✓"
                          : node.masteryLevel === "advanced"
                            ? "Nearly sealed"
                            : `${node.masteryLevel.charAt(0).toUpperCase() + node.masteryLevel.slice(1)}`}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-black border-2 border-gray-400"></div>
            <span className="text-gray-600 dark:text-gray-400">
              Chaos (Not started)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gray-500 border-2 border-gray-400"></div>
            <span className="text-gray-600 dark:text-gray-400">
              In Progress
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-300"></div>
            <span className="text-gray-600 dark:text-gray-400">
              Sealed ✓
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
